<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="hrissecondary"
        class="white--text px-4"
        v-on="on"
      >
        Create
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Type of Misconduct"
      text="Create Type of Misconduct"
      class="mb-0"
    > 
      <form>
        <v-container>
          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                v-model="code"
                v-validate="'required'"
                :error-messages="errors.collect('code')"
                data-vv-name="code"   
                outlined             
                data-vv-as="type of misconduct code"                
                label="Type of Misconduct Code"
                placeholder="Code (e.g., 1.1.0)"
                class="mt-2"
              />
            </v-col>

            <v-col
              v-if="typeOfMisconductRevisions.length > 0"
              cols="12"
            >
              <v-autocomplete
                v-model="version"
                v-validate="'required'"
                :error-messages="errors.collect('version')"
                data-vv-name="version"  
                :items="typeOfMisconductRevisions"
                label="Version"
                item-text="name"
                item-value="id"
                outlined
              />
            </v-col>
            <v-row
              v-else
              no-gutters
            >
              <v-col
                cols="7"
                class="py-0"
              >
                <v-text-field
                  v-model="leadingZero"
                  v-validate="'required|integer|min_value:0|max_value:5'"
                  :error-messages="errors.collect('leadingZero')"
                  data-vv-name="leadingZero"
                  data-vv-as="number of leading zero"
                  type="number"
                  label="Number of leading zero(s)"
                  outlined
                  :min="0"
                  :max="5"
                  @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                />
              </v-col>
              <v-col
                cols="5"
                class="pl-4"
              >
                <v-text-field
                  v-model="newVersionFormat"
                  label="Version"
                  readonly
                >
                  <v-tooltip 
                    slot="append"
                    bottom
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon
                        color="warning"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>The version starter follow the setting value for VERSION_STARTER in setup</span>
                  </v-tooltip>
                </v-text-field>
              </v-col>
              <v-col
                cols="12"
                class="py-0"
              >
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="startDateDisplay"
                      v-validate="'required'"
                      label="Version Start Date"
                      :error-messages="errors.collect('startDateDisplay')"
                      data-vv-name="startDateDisplay"  
                      data-vv-as="start date"    
                      readonly
                      outlined
                      class="label-position"
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    @input="startDateMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>

            <v-col cols="12">
              <v-autocomplete
                v-model="group"
                :items="jobGroupPolicies"
                label="Job Group Policy"
                item-text="name"
                item-value="id"
                outlined
                clearable
              />
            </v-col>
            
            <v-col cols="12">
              <tiptap-vuetify
                v-model="name"
                v-validate="'required'"
                :extensions="extensions"
                class="full-width text-editor-toolbar-height pb-1"
                :error-messages="errors.collect('name')"
                data-vv-name="name"
                data-vv-as="type of misconduct"
                placeholder="Type of Misconduct"
              />
            </v-col>
          </v-row>
        </v-container>
      </form>
      
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"  
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary" 
          class="white--text px-10"        
          @click="submitForm"
        >
          Create
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { TiptapVuetify, Heading, Bold, Italic, Underline, BulletList, OrderedList, ListItem, Link, HardBreak } from 'tiptap-vuetify'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    components: {
      TiptapVuetify,
    },
    props: {
      versionStarter: {
        type: String,
        default: '',
      },
      jobGroupPolicies: {
        type: Array,
        default: null,
      },
      typeOfMisconductRevisions: {
        type: Array,
        default: null,
      },
    },
    data () {
      return {
        progressing: false,
        dialog: null,
        name: null,
        code: null,
        version: null,
        group: null,
        startDateMenu: false,
        startDate: null,
        extensions: [
          Link,
          Underline,
          Italic,
          ListItem,
          BulletList,
          OrderedList,
          [Heading, {
            options: {
              levels: [1, 2, 3],
            },
          }],
          Bold,
          HardBreak,
        ],
        leadingZero: 1,
      }
    },
    computed: {
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      newVersionFormat () {
        const firstVersion = '1'
        const length = (Number(this.leadingZero) + 1)

        return this.versionStarter + firstVersion.padStart(length, '0')
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return this.$resetData()

        this.$validator.extend('codeFormat', {
          getMessage (field, args) {
            return 'Invalid code. Only numbers and . allowed; not allowing two consecutive special characters; not allowing start and end with special character'
          },
          validate (value, args) {
            const regex = /^(?![-_.])(?!.*[-_.]{2})(?!.*[-_.]$)[0-9.]*$/
            return regex.test(value)
          },
        })
      },
    },
    methods: { 
      prepareFormData () {
        if (this.name.startsWith('<p>') && this.name.endsWith('</p>')) {
          const removeLeadingtag = this.name.substring(3)
          this.name = removeLeadingtag.slice(0, -4)
        }

        if (this.startDate) {
          return {
            name: this.name,
            code: this.code,
            start_date: this.startDate,
            number_of_leading_zero: this.leadingZero,
            job_group_policy_id: (this.group === 'undefined' || this.group === 'null') ? null : this.group,
          }
        } else {
          return {
            name: this.name,
            code: this.code,
            type_of_misconduct_revision_id: this.version,
            job_group_policy_id: (this.group === 'undefined' || this.group === 'null') ? null : this.group,
          }
        }
      },   
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true
    
            this.$api.typeOfMisconducts().create(this.prepareFormData())
              .then((res) => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('created', res.data)                
                  this.$snack({ message: res.data.status })

                  this.$validator.reset()
                  this.dialog = false               
                }
              })
              .catch((err) => {
                if (err.response.status === 422) {              
                  this.$snack({ 
                    color: 'error',
                    message: err.response.data.message, 
                  })
                } else {
                  this.$snack({ color: 'error', message: 'Unable to create type of misconduct' })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
  
    },
  }
</script>

<style scoped>
.text-editor-toolbar-height>>> .v-toolbar__content{
  height: unset !important;
  min-height: 42px;
}
.text-editor-toolbar-height>>>.v-sheet.v-sheet--tile.theme--light.v-toolbar.v-toolbar--dense.v-toolbar--flat.grey.lighten-4{
  min-height:unset !important;
}
.label-position >>> .v-label.v-label--active{
  margin-left:-28px
}
.full-width{
  width: 100%;
}
</style>
